import store from '@/store';
import StorageService from './StorageService';
import * as arobaPublicHttp from './api/aroba-oapi-http.service';
import { Auth } from 'aws-amplify';
import jwtDecode from 'jwt-decode';

import { HTTPHeaders, HistoryCode } from '@/globals/enums';
import { STORAGE_KEYS } from '@/constants/storage';
import { errorMessages } from '@/globals/errorMessages';
import arobaAxiosInstance from '@/services/axios/aroba-axios.service';

import {
  changeEmail as changeEmailAPI,
  getUserMngId,
  resetMFA,
  resetMFAToken,
} from './user.service';
import { axiosErrorWrapper } from '@/utils/axios.util';
import {
  getCognitoIdToken,
  handleCognitoError,
  isCognitoSessionValid,
} from './cognito.service';
import { logEvent } from './log.service';

export default class AuthService {
  initLogin() {
    const token = StorageService.getItem(STORAGE_KEYS.SCMCognitoToken);
    if (token) {
      store.dispatch('settings/changeLoggedIn', true);
      store.dispatch('settings/changeImgId', token.img_file_id);
    }
  }

  /**
   * AWS Cognitoの認証を行う
   * @param {String} emailAddress
   * @param {Strin} password
   * @returns
   */
  async signIn(
    emailAddress,
    password,
    options = { congnitoErrorFn: (error) => handleCognitoError(error) }
  ) {
    try {
      const user = await Auth.signIn(emailAddress, password);
      if (user) {
        if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
          Auth.user = user;
          return { user, status: user.challengeName };
        } else if (
          user.challengeName === 'SMS_MFA' ||
          user.challengeName === 'SOFTWARE_TOKEN_MFA'
        ) {
          return {
            status: user.challengeName,
            user,
          };
        } else if (user.challengeName === 'MFA_SETUP') {
          // This happens when the MFA method is TOTP
          // The user needs to setup the TOTP before using it
          // More info please check the Enabling MFA part
          await Auth.setupTOTP(user);
        }

        return { user, status: 'NO_ACTION' };
      }

      return { status: 'NO_ACTION' };
    } catch (err) {
      options.congnitoErrorFn(err);
      return { status: 'ERROR', error: err };
    }
  }

  async scmSignIn(user, avatarId) {
    const sessionResult = await isCognitoSessionValid();
    if (!sessionResult.valid) {
      handleCognitoError(sessionResult.error);
      throw sessionResult.error;
    }
    if (user) {
      const userEmail = await this.getCurrentUserEmail();
      const tempCred = await this.getClientTemporaryCredentials(userEmail);
      const { username, password } =
        await this.acceptClientTemporaryCredentials(
          tempCred.tempraryClientCredentials
        );
      const b64Cred = btoa(`${username}:${password}`);
      // Store Credentials in base64
      StorageService.setItem(STORAGE_KEYS.basicAuth, `Basic ${b64Cred}`);
    }
    await this.getCognitoTokenHeaders(avatarId);

    store.dispatch('settings/changeLoggedIn', true);
  }

  /**
   * Sign in with SCM with retry logic for specific error
   * @param {Object} user - Cognito user object
   * @param {number} avatarId - Avatar ID
   * @returns {Promise<void>}
   */
  async scmSignInWithRetry(user, avatarId) {
    let storedAvatarId = avatarId;
    let scmSignInSuccess = false;
    let retryCount = 0;
    const maxRetries = 2;

    while (!scmSignInSuccess && retryCount < maxRetries) {
      try {
        await this.scmSignIn(user, storedAvatarId);
        scmSignInSuccess = true;

        // if it was a retry and it succeeded, update recent avatars
        if (retryCount > 0) {
          StorageService.updateRecentAvatars(null);
        }
      } catch (error) {
        // if error is HTTP 400 with errorCode 11004, retry
        if (
          error.status === 400 &&
          error.errorCode === 11004 &&
          retryCount < maxRetries - 1
        ) {
          storedAvatarId = null;
          retryCount++;
        } else {
          throw error;
        }
      }
    }
  }

  /**
   * Signout cognito
   */
  signOut = async () => {
    try {
      await Auth.signOut();
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  };

  /**
   * Get current user information
   */
  async getCurrentUserInfo() {
    try {
      const result = await Auth.currentUserInfo();
      return result;
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUnAuthenticatedUser() {
    try {
      const result = await Auth.user;
      return result;
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  /**
   * Get current authenticated user
   */
  async currentAuthenticatedUser() {
    try {
      const result = await Auth.currentAuthenticatedUser();
      return result;
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  // Get the idToken
  async getIdToken() {
    try {
      return getCognitoIdToken();
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  async getIamAccessKey(cameraIds) {
    const payload = {};
    if (cameraIds) {
      payload.cameraIds = cameraIds;
      payload.isFeature = true;
    }
    const idToken = await this.getIdToken();

    try {
      const url =
        process.env.VUE_APP_API_BASE_URL + '/ccs/v1/user/iamAccessKey';

      const res = await arobaAxiosInstance.post(url, payload, {
        headers: {
          [HTTPHeaders.XAuthorization]: idToken,
        },
      });
      const iamRes = res.data;
      store.dispatch('common/changeIam', iamRes);
      return iamRes;
    } catch (error) {
      axiosErrorWrapper(error);
      throw new Error(errorMessages.API_ERROR.error.message);
    }
  }

  async getAuthHeaders() {
    const idToken = await this.getIdToken();
    const token = StorageService.getItem(STORAGE_KEYS.SCMCognitoToken);
    return {
      [HTTPHeaders.XAuthorization]: 'Bearer ' + idToken,
      [HTTPHeaders.Authorization]: StorageService.getItem(
        STORAGE_KEYS.basicAuth
      ),
      [HTTPHeaders.XArobaViewUserToken]: token.accessToken,
    };
  }

  /**
   * Get current cognito session
   */
  getCurrentCognitoSession() {
    return Auth.currentSession();
  }

  async forgotPassword(username) {
    try {
      const result = await Auth.forgotPassword(username);
      return result;
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  async submitForgotPassword(username, code, newPassword) {
    try {
      const result = await Auth.forgotPasswordSubmit(
        username,
        code,
        newPassword
      );
      return result;
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  resetPassword() {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ status: 200 });
      }, 2000);
    });
  }

  async logout(options = {}) {
    const { skipLogging = false } = options;
    if (!skipLogging) {
      try {
        await logEvent(HistoryCode.Logout);
      } catch (error) {
        console.error('Failed to log logout event:', error);
      }
    }
    await this.signOut();
    store.dispatch('auth/logout', false);
  }

  /**
   * Get totp code
   * @returns
   */
  async getTOTPCode(email) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      // To setup TOTP, first you need to get a `authorization code` from Amazon Cognito
      // `user` is the current Authenticated user

      const code = await Auth.setupTOTP(user);
      const str =
        `otpauth://totp/SECOM%20Sights:${email}` +
        '?secret=' +
        code +
        '&issuer=' +
        'SECOM%20Sights';
      return { str, code };
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  /**
   * Verify TOTP code
   * @param {string} code
   * @returns
   */
  async verifyTOTP(code) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const result = await Auth.verifyTotpToken(user, code);
      await this.setMFA('TOTP');
      return result;
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  async setMFA(code) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.setPreferredMFA(
        user,
        code // 'SMS' or 'TOTP' or 'NOMFA'
      );
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  async setUserPhone(user, phone) {
    try {
      await Auth.updateUserAttributes(user, {
        phone_number: phone,
      });
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  async updateUserPhone(newPhoneNumber) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, {
        phone_number: newPhoneNumber,
      });
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  async changeEmail(oldEmail, newEmail) {
    const { user_mng_id } = await getUserMngId(encodeURIComponent(oldEmail));
    await changeEmailAPI(user_mng_id, {
      mail_address: newEmail,
    });
  }

  resetUserMFA(username) {
    return resetMFA({
      username,
    });
  }

  resetUserMFAByToken(token) {
    return resetMFAToken({
      token,
    });
  }

  async getUserPhone() {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const userAttrs = await Auth.userAttributes(user);
      const attr = userAttrs.find((item) => item.Name === 'phone_number');
      if (attr) {
        return attr.Value;
      }
      return null;
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  async sendPhoneVerifyCode() {
    try {
      const result = await Auth.verifyCurrentUserAttribute('phone_number');
      return result;
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  async veifyPhoneCode(code) {
    try {
      const result = await Auth.verifyCurrentUserAttributeSubmit(
        'phone_number',
        code
      );
      return result;
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  }

  completeNewPassword = async (user, newPassword) => {
    try {
      const newUser = await Auth.completeNewPassword(user, newPassword);
      return { user: newUser, status: 'COMPLETE_NEW_PASSWORD' };
    } catch (error) {
      handleCognitoError(error);
      return { status: 'ERROR', error };
    }
  };

  /**
   * Confirn sign in with TOTP code
   */
  async confirmSignInCode(user, code, mfaType) {
    let result = null;
    try {
      // You need to get the code from the UI inputs
      // and then trigger the following function with a button click
      // const code = getCodeFromUserInput();
      // If MFA is enabled, sign-in should be confirmed with the confirmation code
      result = await Auth.confirmSignIn(
        user, // Return object from Auth.signIn()
        code, // Confirmation code
        mfaType // MFA Type e.g. SMS_MFA, SOFTWARE_TOKEN_MFA
      );
    } catch (error) {
      if (error.code === 'NotAuthorizedException') {
        error.code = 'NotAuthorizedException.AuthCode';
      }
      handleCognitoError(error);
      throw error;
    }
    if (result) {
      await this.scmSignIn(result);
    }
  }

  async getCurrentUserEmail() {
    const idToken = await this.getIdToken();
    return jwtDecode(idToken).email;
  }

  /**
   * Confirm client temporary credentials
   */
  async acceptClientTemporaryCredentials(cred) {
    const payload = {
      tempraryClientCredentials: cred,
      appType: 1,
    };
    const result = await arobaPublicHttp.post(
      `${process.env.VUE_APP_API_BASE_URL}/ccs/v1/user/client/credentials/accept`,
      payload
    );
    return result;
  }

  /**
   * Get client temporary credentials
   */
  async getClientTemporaryCredentials(email) {
    const clientUuid = self.crypto.randomUUID();
    const payload = {
      appType: 1,
      clientUuid,
      userAccessKey: email,
    };
    const result = await arobaPublicHttp.post(
      `${process.env.VUE_APP_API_BASE_URL}/ccs/v1/user/client/credentials/temporary`,
      payload
    );
    return result;
  }

  /**
   * Get cognito token headers
   */
  async getCognitoTokenHeaders(avatarId) {
    const idToken = await this.getIdToken();
    const payload = {
      userAccessKey: jwtDecode(idToken).email,
      providerType: 1,
    };
    if (avatarId) {
      payload.avatarId = avatarId;
    }

    Object.setPrototypeOf(payload, {
      __headers: {
        [HTTPHeaders.XAuthorization]: idToken,
        [HTTPHeaders.Authorization]: StorageService.getItem(
          STORAGE_KEYS.basicAuth
        ),
      },
    });
    const result = await arobaPublicHttp.post(
      `${process.env.VUE_APP_API_BASE_URL}/ccs/v1/user/cognitoAuth`,
      payload,
      (err) => !(err.status === 400 && err.errorCode === 11004)
    );
    StorageService.setItem(STORAGE_KEYS.SCMCognitoToken, result);
    return result;
  }

  /**
   * Get current user MFA setting
   */
  getCurrentUserMFA = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      // Will retrieve the current mfa type from cache
      const result = await Auth.getPreferredMFA(user, {
        // Optional, by default is false.
        // If set to true, it will get the MFA type from server side instead of from local cache.
        bypassCache: false,
      });
      return result;
    } catch (error) {
      handleCognitoError(error);
      throw error;
    }
  };
}

export const authService = new AuthService();
